import React, { useState, useEffect } from 'react';
import { useCurrency } from '../../hooks/useCurrency';
import { Home, Search, Download, Printer } from 'lucide-react';
import { supabase } from '../../lib/supabase';

interface DueReportItem {
  id: string;
  invoice_number: string;
  patient_id: string;
  patient_name: string;
  patient_unique_id: string;
  patient_phone: string;
  issue_date: string;
  due_date: string;
  total: number;
  amount_paid: number;
  balance_due: number;
  days_overdue: number;
  status: string;
}

export default function DueReport() {
  const { formatCurrency } = useCurrency();
  const [reportData, setReportData] = useState<DueReportItem[]>([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [startDate, setStartDate] = useState('');
  const [endDate, setEndDate] = useState('');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  useEffect(() => {
    loadDueReport();
  }, [startDate, endDate, statusFilter]);

  const loadDueReport = async () => {
    try {
      setLoading(true);
      
      const url = import.meta.env.VITE_SUPABASE_URL;
      if (!url || url === 'https://placeholder.supabase.co') {
        setReportData(getMockDueReport());
        setLoading(false);
        return;
      }

      let query = supabase
        .from('invoices')
        .select(`
          id,
          invoice_number,
          patient_id,
          issue_date,
          due_date,
          total,
          amount_paid,
          balance_due,
          status,
          patient:patients(id, name, unique_id, phone)
        `)
        .gt('balance_due', 0);

      if (statusFilter !== 'all') {
        query = query.eq('status', statusFilter);
      }
      if (startDate) {
        query = query.gte('due_date', startDate);
      }
      if (endDate) {
        query = query.lte('due_date', endDate);
      }

      const { data, error } = await query.order('due_date', { ascending: true });
      if (error) throw error;

      const formattedData: DueReportItem[] = (data || []).map(item => {
        const dueDate = new Date(item.due_date);
        const today = new Date();
        const daysOverdue = Math.max(0, Math.floor((today.getTime() - dueDate.getTime()) / (1000 * 60 * 60 * 24)));
        
        return {
          id: item.id,
          invoice_number: item.invoice_number,
          patient_id: item.patient_id,
          patient_name: item.patient?.name || 'N/A',
          patient_unique_id: item.patient?.unique_id || 'N/A',
          patient_phone: item.patient?.phone || 'N/A',
          issue_date: item.issue_date,
          due_date: item.due_date,
          total: item.total,
          amount_paid: item.amount_paid,
          balance_due: item.balance_due,
          days_overdue: daysOverdue,
          status: item.status,
        };
      });

      setReportData(formattedData);
    } catch (error) {
      console.error('Error loading due report:', error);
      setReportData(getMockDueReport());
    } finally {
      setLoading(false);
    }
  };

  const getMockDueReport = (): DueReportItem[] => {
    return [
      {
        id: '1',
        invoice_number: 'INV-2025-001',
        patient_id: '1',
        patient_name: 'John Doe',
        patient_unique_id: 'P-001',
        patient_phone: '01712345678',
        issue_date: '2025-01-15',
        due_date: '2025-02-15',
        total: 5000,
        amount_paid: 2000,
        balance_due: 3000,
        days_overdue: 0,
        status: 'partial',
      },
      {
        id: '2',
        invoice_number: 'INV-2025-002',
        patient_id: '2',
        patient_name: 'Jane Smith',
        patient_unique_id: 'P-002',
        patient_phone: '01798765432',
        issue_date: '2025-01-10',
        due_date: '2025-01-25',
        total: 7500,
        amount_paid: 0,
        balance_due: 7500,
        days_overdue: 12,
        status: 'overdue',
      },
      {
        id: '3',
        invoice_number: 'INV-2025-003',
        patient_id: '3',
        patient_name: 'Ahmed Khan',
        patient_unique_id: 'P-003',
        patient_phone: '01856789012',
        issue_date: '2025-01-20',
        due_date: '2025-02-20',
        total: 3500,
        amount_paid: 1500,
        balance_due: 2000,
        days_overdue: 0,
        status: 'partial',
      },
    ];
  };

  const filteredData = reportData.filter(item => {
    const matchesSearch = 
      item.invoice_number.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_unique_id.toLowerCase().includes(searchTerm.toLowerCase()) ||
      item.patient_phone.includes(searchTerm);
    return matchesSearch;
  });

  const totalDue = filteredData.reduce((sum, item) => sum + item.balance_due, 0);
  const totalInvoices = filteredData.length;

  const handleExportCSV = () => {
    const headers = ['Invoice #', 'Patient ID', 'Patient Name', 'Phone', 'Issue Date', 'Due Date', 'Total', 'Paid', 'Balance Due', 'Days Overdue', 'Status'];
    const rows = filteredData.map(item => [
      item.invoice_number,
      item.patient_unique_id,
      item.patient_name,
      item.patient_phone,
      item.issue_date,
      item.due_date,
      item.total,
      item.amount_paid,
      item.balance_due,
      item.days_overdue,
      item.status,
    ]);

    const csvContent = [headers, ...rows].map(row => row.join(',')).join('\n');
    const blob = new Blob([csvContent], { type: 'text/csv' });
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = `due-report-${new Date().toISOString().split('T')[0]}.csv`;
    a.click();
  };

  const handlePrint = () => {
    window.print();
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <div className="bg-white border-b border-gray-200 px-6 py-4">
        <div className="flex items-center justify-between">
          <div className="flex items-center space-x-2 text-sm text-gray-600">
            <Home className="w-4 h-4" />
            <span>/</span>
            <span>Billings/Reception</span>
            <span>/</span>
            <span className="text-gray-900 font-medium">Due Report</span>
          </div>
          <div className="flex items-center space-x-3">
            <button
              onClick={handleExportCSV}
              className="flex items-center space-x-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 transition-colors"
            >
              <Download className="w-4 h-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
            >
              <Printer className="w-4 h-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      <div className="max-w-none px-6 py-8">
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-6">
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Invoices with Due</p>
            <p className="text-2xl font-bold text-gray-900 mt-1">{totalInvoices}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Total Outstanding Amount</p>
            <p className="text-2xl font-bold text-red-600 mt-1">{formatCurrency(totalDue)}</p>
          </div>
          <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-6">
            <p className="text-sm text-gray-600">Overdue Invoices</p>
            <p className="text-2xl font-bold text-orange-600 mt-1">
              {filteredData.filter(item => item.days_overdue > 0).length}
            </p>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 p-4 mb-6">
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-5 h-5" />
              <input
                type="text"
                placeholder="Search invoice, patient..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              >
                <option value="all">All Status</option>
                <option value="partial">Partial</option>
                <option value="overdue">Overdue</option>
                <option value="sent">Sent</option>
              </select>
            </div>
            <div>
              <input
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                placeholder="Start Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
            <div>
              <input
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                placeholder="End Date"
                className="w-full px-3 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-teal-500 focus:border-transparent"
              />
            </div>
          </div>
        </div>

        <div className="bg-white rounded-lg shadow-sm border border-gray-200 overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full">
              <thead className="bg-gray-50 border-b border-gray-200">
                <tr>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Invoice #
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Patient
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Phone
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Due Date
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Total
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Paid
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Balance Due
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Days Overdue
                  </th>
                  <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                    Status
                  </th>
                </tr>
              </thead>
              <tbody className="bg-white divide-y divide-gray-200">
                {loading ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      Loading...
                    </td>
                  </tr>
                ) : filteredData.length === 0 ? (
                  <tr>
                    <td colSpan={9} className="px-6 py-4 text-center text-gray-500">
                      No due invoices found
                    </td>
                  </tr>
                ) : (
                  filteredData.map((item) => (
                    <tr key={item.id} className="hover:bg-gray-50">
                      <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900">
                        {item.invoice_number}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900">
                        <div>
                          <div className="font-medium">{item.patient_name}</div>
                          <div className="text-gray-500 text-xs">{item.patient_unique_id}</div>
                        </div>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {item.patient_phone}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500">
                        {new Date(item.due_date).toLocaleDateString()}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-900 font-medium">
                        {formatCurrency(item.total)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-green-600">
                        {formatCurrency(item.amount_paid)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm text-red-600 font-bold">
                        {formatCurrency(item.balance_due)}
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap text-sm">
                        <span className={`font-medium ${item.days_overdue > 0 ? 'text-red-600' : 'text-gray-500'}`}>
                          {item.days_overdue} days
                        </span>
                      </td>
                      <td className="px-6 py-4 whitespace-nowrap">
                        <span
                          className={`px-2 py-1 text-xs font-medium rounded-full ${
                            item.status === 'overdue'
                              ? 'bg-red-100 text-red-800'
                              : 'bg-yellow-100 text-yellow-800'
                          }`}
                        >
                          {item.status}
                        </span>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
